// AdminGoogleMerchantPage — copy-ready feed URLs for Google Merchant Center.
// Operators paste each URL into GMC → Products → Feeds → Add primary feed
// → Scheduled fetch. Six feeds (XML/TSV × EUR/USD/GBP) built from the live
// Gelato catalog with FX-converted prices.
import { useEffect, useState } from "react";
import { Loader2, Copy, Check, ExternalLink, ShoppingBag } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminGoogleMerchantPage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/shop/google-merchant/feeds`, { credentials: "include" })
      .then((r) => r.json())
      .then(setData)
      .catch((e) => setError(e.message || String(e)));
  }, []);

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(url);
      setTimeout(() => setCopied(null), 1500);
    } catch (e) {
      alert("Impossible de copier — sélectionnez manuellement le lien.");
    }
  };

  if (error) return <div className="p-8 text-red-600" data-testid="gmc-error">Erreur : {error}</div>;
  if (!data) return (
    <div className="p-12 flex items-center justify-center" data-testid="gmc-loading">
      <Loader2 className="animate-spin text-[color:var(--gv-pink)]" size={24} />
    </div>
  );

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="admin-gmc-page">
      <header className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <ShoppingBag size={28} className="text-[color:var(--gv-pink)]" />
          <h1 className="text-4xl font-serif" data-testid="gmc-title">Google Merchant Center — Flux produits</h1>
        </div>
        <p className="text-sm text-slate-600 max-w-3xl">
          Six flux prêts à coller dans Google Merchant Center (ID <span className="font-mono text-slate-800">{data.merchant_id}</span>).
          Un flux <strong>primaire</strong> par (format × devise × pays). Format recommandé : <strong>XML</strong>.
          TSV en secours si la validation XML échoue.
        </p>
        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded text-sm text-slate-700 leading-relaxed">
          <strong>Comment l'ajouter :</strong> Merchant Center → Produits → Flux → « + Ajouter un flux principal »
          → cochez « Ajouter des produits à partir d'un fichier » → « Saisir le lien du fichier » → collez l'URL ci-dessous
          → Planning : synchronisation automatique 24h à 00:00.
        </div>
      </header>

      <div className="space-y-4">
        {data.feeds.map((feed) => (
          <div
            key={feed.url}
            className="bg-white border border-slate-200 rounded p-5 flex items-center gap-4"
            data-testid={`gmc-feed-${feed.format}-${feed.currency}`}
          >
            <div className="flex-shrink-0 w-16 h-16 bg-slate-50 rounded flex items-center justify-center">
              <span className="text-2xl font-serif text-slate-800">{feed.currency}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="uppercase text-xs font-bold tracking-wider text-[color:var(--gv-pink)]">
                  {feed.format}
                </span>
                <span className="text-slate-400">·</span>
                <span className="text-sm text-slate-600">
                  Pays cible : <span className="font-mono">{feed.countries.join(", ")}</span>
                </span>
              </div>
              <code className="block text-xs font-mono text-slate-800 truncate bg-slate-50 px-3 py-2 rounded">
                {feed.url}
              </code>
            </div>
            <div className="flex-shrink-0 flex items-center gap-2">
              <button
                onClick={() => copy(feed.url)}
                className="px-3 py-2 bg-[color:var(--gv-pink)] text-white rounded hover:opacity-90 transition inline-flex items-center gap-2 text-sm"
                data-testid={`gmc-copy-${feed.format}-${feed.currency}`}
              >
                {copied === feed.url ? <><Check size={16} /> Copié</> : <><Copy size={16} /> Copier</>}
              </button>
              <a
                href={feed.url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 border border-slate-300 rounded hover:bg-slate-50 transition inline-flex items-center gap-2 text-sm"
                data-testid={`gmc-open-${feed.format}-${feed.currency}`}
              >
                <ExternalLink size={16} /> Ouvrir
              </a>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 p-5 bg-slate-50 border border-slate-200 rounded text-sm text-slate-700 space-y-2">
        <p><strong>Notes techniques :</strong></p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Prix EUR = source directe Gelato · USD/GBP = conversion temps réel via taux ECB (frankfurter.dev).</li>
          <li>Cache serveur 6h — Google ne « voit » un changement de prix Gelato qu'au maximum 6h plus tard.</li>
          <li>Chaque variant (produit × couleur × taille) est un <code className="font-mono text-xs">&lt;item&gt;</code> séparé,
              regroupé par <code className="font-mono text-xs">item_group_id</code>. Environ 260 items publiés.</li>
          <li>Attribut <code className="font-mono text-xs">identifier_exists=no</code> (pas de GTIN/EAN sur les affiches Gelato).</li>
          <li>Marque : <code className="font-mono text-xs">geoVINUM</code> · Catégorie Google : 500044 (Posters/Prints).</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminGoogleMerchantPage;
